import { visionLabApi } from '@/app/apiClient';
import { ApiHttpError } from '@/shared/api/client';
import type { ApiCallOptions } from '@/shared/api/client';
import type { DedupPrecheckOutcome } from '../types/uploads';
import { putGoogleStorageSignedFrame } from './googleStorage';

/** Result of the pre-upload exact-duplicate check. `skip` = the check could not be performed (e.g. the
 * caller is not authorized to read assets by hash); the upload proceeds and the authoritative dedup
 * still runs post-upload in intake. */
export type DedupResult = 'exists' | 'absent' | 'skip';

/**
 * Exact-duplicate pre-check against the existing `GET /assets/by-content-hash/:hash` endpoint:
 * `200` → the content already exists as an asset (`exists`, block before uploading a byte);
 * `404` → not present (`absent`, proceed); any other status → `skip` (don't block).
 */
export async function checkContentHash(
  hash: string,
  options: ApiCallOptions = {},
): Promise<DedupResult> {
  try {
    await visionLabApi.get(`/assets/by-content-hash/${encodeURIComponent(hash)}`, {
      ...options,
      responseType: 'void',
    });
    return 'exists';
  } catch (e) {
    if (e instanceof ApiHttpError && e.status === 404) return 'absent';
    return 'skip';
  }
}

// ── PDQ near-duplicate pre-check (gate 2) ──────────────────────────────────────────────────────────

const seg = (id: string) => encodeURIComponent(id);

export const MIN_DEDUP_FRAME_COUNT = 3;
export const MAX_DEDUP_FRAME_COUNT = 8;

function requireFrameCount(count: number): void {
  if (!Number.isInteger(count) || count < MIN_DEDUP_FRAME_COUNT || count > MAX_DEDUP_FRAME_COUNT) {
    throw new RangeError(
      `PDQ frame count must be an integer from ${MIN_DEDUP_FRAME_COUNT} through ${MAX_DEDUP_FRAME_COUNT}`,
    );
  }
}

/** One signed GCS PUT URL for the sample frame at extracted-array index `index`. */
interface DedupFrameUrl {
  index: number;
  uploadUrl: string;
}

export interface DedupFrameUrlsResponse {
  frames: DedupFrameUrl[];
}

/**
 * Ask the backend for `count` signed GCS PUT URLs to upload the client-extracted PDQ sample frames.
 * The frame at extracted-array index `i` maps to the returned entry with matching `index`.
 */
export async function requestDedupFrameUrls(
  sessionId: string,
  count: number,
  options: ApiCallOptions = {},
): Promise<DedupFrameUrlsResponse> {
  requireFrameCount(count);
  return visionLabApi.post<DedupFrameUrlsResponse>(
    `/submissions/${seg(sessionId)}/dedup-frames-url`,
    {
      ...options,
      json: { count },
    },
  );
}

export interface DedupCheckResponse {
  outcome: DedupPrecheckOutcome;
}

/**
 * PUT one JPEG sample frame directly to its signed GCS URL. This bypasses `visionLabApi.put` on purpose:
 * a signed URL carries its own auth in the query string and must NOT get a Bearer header or the `/api`
 * prefix. Throws on any non-2xx so the caller can request one fresh signed plan, then explicitly
 * classify exhausted transient failures as `inconclusive`.
 */
export async function putSignedFrame(uploadUrl: string, blob: Blob): Promise<void> {
  return putGoogleStorageSignedFrame(uploadUrl, blob);
}

/**
 * Ask the backend to hash the `count` uploaded sample frames with its PDQ sidecar and compare them
 * against the stored frame hashes within a Hamming threshold, returning the redacted three-state
 * advisory. The response never carries a matching asset identity.
 */
export async function checkNearDuplicate(
  sessionId: string,
  count: number,
  options: ApiCallOptions = {},
): Promise<DedupCheckResponse> {
  requireFrameCount(count);
  return visionLabApi.post<DedupCheckResponse>(`/submissions/${seg(sessionId)}/dedup-check`, {
    ...options,
    json: { count },
  });
}
